'use client';

import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'next/navigation';
import React, { useEffect } from 'react';

import { usePrevious } from '@chakra-ui/react';
import Loading from '@components/common/Loading';
import { useMeQuery } from 'generated/graphql';
import { setOrgCookieClient } from 'helpers/cookies';
import { useOrgSlug } from 'hooks/useOrgSlug';
import { useStores } from 'stores/rootStore';
import { LoginStatus } from 'stores/userStore';

interface Props {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

const AppInitializer = ({ children, isAuthenticated }: Props) => {
  const orgSlug = useOrgSlug();
  const previousOrgSlug = usePrevious(orgSlug);
  const [result] = useMeQuery({
    pause: !isAuthenticated,
    variables: {
      orgSlug,
    },
    requestPolicy: 'cache-first',
  });

  const params = useSearchParams();
  const { userStore, orgStore } = useStores();

  useEffect(() => {
    if (userStore.status === LoginStatus.LoggedIn && previousOrgSlug === orgSlug) {
      return;
    }

    const loggedInUser = result.data?.me;
    if (loggedInUser != null) {
      const isNew = params.get('isNew') === 'true';
      userStore.loggedIn(loggedInUser, isNew);
      const userOrgs = loggedInUser.orgs ?? [];
      const orgs = userOrgs.map((org) => ({
        id: org.id,
        slug: org.slug,
        name: org.name,
        isActive: org.isActive,
      }));
      orgStore.setOrgs(orgs);
      const selectedOrg = userOrgs.find((org) => org.slug.toLowerCase() === orgSlug?.toLowerCase());
      if (selectedOrg != null) {
        orgStore.setSelectedOrg(selectedOrg.id);
        setOrgCookieClient(selectedOrg.slug);
      }
    }
  }, [result.data?.me, userStore, params, orgStore, orgSlug, previousOrgSlug]);

  if (result.fetching) {
    return <Loading />;
  }

  return children;
};

export default observer(AppInitializer);
